import { useEffect, useMemo, useRef, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  FiArchive,
  FiChevronDown,
  FiChevronUp,
  FiEdit3,
  FiLayers,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUnlock,
  FiX,
} from "react-icons/fi";
import { recalculateRecordAfterEventFeeChange } from "./utils/feeRules";
import "./AdminEventSetup.css";

const getEventName = (event) =>
  String(event?.name || event?.eventName || "ไม่ระบุชื่องาน").trim();

const getTickets = (event) => {
  const tickets = event?.tickets || event?.prices || [];
  return Array.isArray(tickets) ? tickets : [];
};

const formatMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return `฿${number.toLocaleString("th-TH", {
    maximumFractionDigits: 2,
  })}`;
};

const ARCHIVE_RETENTION_DAYS = 30;
const ARCHIVE_RETENTION_MS = ARCHIVE_RETENTION_DAYS * 24 * 60 * 60 * 1000;

const toDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getArchiveDaysLeft = (event) => {
  if (!event?.archived) return null;
  const archivedAt = toDate(event.archivedAt);
  if (!archivedAt) return ARCHIVE_RETENTION_DAYS;
  const elapsed = Date.now() - archivedAt.getTime();
  return Math.max(0, Math.ceil((ARCHIVE_RETENTION_MS - elapsed) / (24 * 60 * 60 * 1000)));
};

const normalizeComparable = (value) => String(value ?? "").trim();

const chunkArray = (items, size = 400) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

export default function AdminEventSetup({ showNotification }) {
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [busyId, setBusyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [showPurgeArchivedConfirm, setShowPurgeArchivedConfirm] = useState(false);
  const [isPurgingArchived, setIsPurgingArchived] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [eventNameDraft, setEventNameDraft] = useState("");
  const [editingTickets, setEditingTickets] = useState([]);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const archiveMaintenanceRef = useRef(new Set());

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "events"),
      (snapshot) => {
        const list = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setEvents(list);
        setLoading(false);
        setLoadError("");
      },
      (error) => {
        console.error("โหลดรายการงานไม่สำเร็จ", error);
        setLoading(false);
        setLoadError("โหลดรายการงานไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      }
    );

    return unsubscribe;
  }, []);

  // งานที่เก็บไว้จะถูกลบเฉพาะ document ใน collection events หลังครบ 30 วัน
  // ประวัติค่ากดของทีมอยู่คนละ collection จึงไม่ถูกลบตาม
  // งานเก่าที่ถูก archive มาก่อนมี field archivedAt จะเริ่มนับ 30 วันตั้งแต่ครั้งแรกที่แอดมินเปิดหน้านี้
  useEffect(() => {
    if (loading || events.length === 0) return;

    const runArchiveMaintenance = async () => {
      const now = Date.now();
      const needsArchiveTimestamp = [];
      const expiredEvents = [];

      events.forEach((event) => {
        if (!event.archived) return;

        const archivedAt = toDate(event.archivedAt);
        if (!archivedAt) {
          const key = `stamp:${event.id}`;
          if (!archiveMaintenanceRef.current.has(key)) {
            archiveMaintenanceRef.current.add(key);
            needsArchiveTimestamp.push(event);
          }
          return;
        }

        if (now - archivedAt.getTime() >= ARCHIVE_RETENTION_MS) {
          const key = `delete:${event.id}`;
          if (!archiveMaintenanceRef.current.has(key)) {
            archiveMaintenanceRef.current.add(key);
            expiredEvents.push(event);
          }
        }
      });

      try {
        for (const chunk of chunkArray(needsArchiveTimestamp)) {
          const batch = writeBatch(db);
          chunk.forEach((event) => {
            batch.update(doc(db, "events", event.id), {
              archivedAt: new Date(),
              archiveRetentionDays: ARCHIVE_RETENTION_DAYS,
            });
          });
          await batch.commit();
        }
      } catch (error) {
        console.error("ตั้งวันที่เริ่มเก็บงานไม่สำเร็จ", error);
        needsArchiveTimestamp.forEach((event) =>
          archiveMaintenanceRef.current.delete(`stamp:${event.id}`)
        );
      }

      try {
        for (const chunk of chunkArray(expiredEvents)) {
          const batch = writeBatch(db);
          chunk.forEach((event) => batch.delete(doc(db, "events", event.id)));
          await batch.commit();
        }
        if (expiredEvents.length > 0) {
          showNotification?.(
            `🧹 ลบงานที่เก็บเกิน ${ARCHIVE_RETENTION_DAYS} วันแล้ว ${expiredEvents.length} งาน`
          );
        }
      } catch (error) {
        console.error("ล้างงานที่เก็บเกินกำหนดไม่สำเร็จ", error);
        expiredEvents.forEach((event) =>
          archiveMaintenanceRef.current.delete(`delete:${event.id}`)
        );
      }
    };

    runArchiveMaintenance();
    // showNotification เป็น callback จาก parent; ไม่ต้องให้การเปลี่ยน reference เรียก maintenance ซ้ำ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, loading]);

  const summary = useMemo(() => {
    const active = events.filter((event) => !event.archived).length;
    const archived = events.filter((event) => event.archived).length;
    const ticketOptions = events.reduce(
      (sum, event) => sum + getTickets(event).length,
      0
    );

    return {
      total: events.length,
      active,
      archived,
      ticketOptions,
    };
  }, [events]);

  const filteredEvents = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    return [...events]
      .filter((event) => {
        if (statusFilter === "active" && event.archived) return false;
        if (statusFilter === "archived" && !event.archived) return false;

        if (!keyword) return true;

        const eventName = getEventName(event).toLowerCase();
        const ticketText = getTickets(event)
          .map((ticket) => `${ticket?.label || ""} ${ticket?.fee || ""}`)
          .join(" ")
          .toLowerCase();

        return eventName.includes(keyword) || ticketText.includes(keyword);
      })
      .sort((a, b) => {
        if (a.archived !== b.archived) return a.archived ? 1 : -1;

        const dateA = a.createdAt?.toDate?.()?.getTime?.() || 0;
        const dateB = b.createdAt?.toDate?.()?.getTime?.() || 0;
        if (dateA !== dateB) return dateB - dateA;

        return getEventName(a).localeCompare(getEventName(b), "th");
      });
  }, [events, searchTerm, statusFilter]);

  const toggleExpanded = (eventId) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const closeEventEditor = () => {
    if (isSavingEdit) return;
    setEditingEvent(null);
    setIsCreatingEvent(false);
    setEventNameDraft("");
    setEditingTickets([]);
  };

  const openCreateEvent = () => {
    if (busyId || isSavingEdit) return;
    setEditingEvent(null);
    setIsCreatingEvent(true);
    setEventNameDraft("");
    setEditingTickets([
      { label: "", fee: "", originalLabel: "", originalFee: "", isExisting: false },
    ]);
  };

  const openEditEvent = (event) => {
    const normalized = getTickets(event).map((ticket) => {
      const label = String(ticket?.label ?? ticket?.price ?? "");
      const fee = String(ticket?.fee ?? "");
      return {
        label,
        fee,
        originalLabel: label,
        originalFee: fee,
        isExisting: true,
      };
    });

    setIsCreatingEvent(false);
    setEditingEvent(event);
    setEventNameDraft(getEventName(event));
    setEditingTickets(
      normalized.length
        ? normalized
        : [{ label: "", fee: "", originalLabel: "", originalFee: "", isExisting: false }]
    );
  };

  const updateEditingTicket = (index, field, value) => {
    setEditingTickets((previous) =>
      previous.map((ticket, ticketIndex) =>
        ticketIndex === index ? { ...ticket, [field]: value } : ticket
      )
    );
  };

  const addEditingTicket = () => {
    setEditingTickets((previous) => [
      ...previous,
      { label: "", fee: "", originalLabel: "", originalFee: "", isExisting: false },
    ]);
  };

  const removeEditingTicket = (index) => {
    setEditingTickets((previous) => {
      if (previous.length === 1) return [{ label: "", fee: "" }];
      return previous.filter((_, ticketIndex) => ticketIndex !== index);
    });
  };

  const saveEventEditor = async () => {
    const isCreateMode = isCreatingEvent && !editingEvent;
    if ((!isCreateMode && !editingEvent) || isSavingEdit || busyId) return;

    const normalizedName = String(
      isCreateMode ? eventNameDraft : getEventName(editingEvent)
    ).trim();

    if (!normalizedName) {
      showNotification?.("❌ กรุณากรอกชื่องาน");
      return;
    }

    const normalizedRows = editingTickets
      .map((ticket) => {
        const label = String(ticket.label || "").trim();
        const feeText = String(ticket.fee ?? "").trim();
        const fee = feeText === "" ? Number.NaN : Number(feeText);
        return {
          label,
          fee,
          feeText,
          originalLabel: String(ticket.originalLabel || "").trim(),
          originalFee: Number(ticket.originalFee),
          isExisting: Boolean(ticket.isExisting),
        };
      })
      .filter((ticket) => ticket.label || ticket.feeText);

    if (normalizedRows.length === 0) {
      showNotification?.("❌ ต้องมีอย่างน้อย 1 โซน/ราคา");
      return;
    }

    const hasInvalid = normalizedRows.some(
      (ticket) => !ticket.label || !Number.isFinite(ticket.fee) || ticket.fee < 0
    );
    if (hasInvalid) {
      showNotification?.("❌ กรุณากรอกโซน/ราคาและค่ากดให้ครบทุกบรรทัด");
      return;
    }

    const duplicateLabel = normalizedRows.find((ticket, index, rows) =>
      rows.findIndex(
        (item) => normalizeComparable(item.label).toLowerCase() === normalizeComparable(ticket.label).toLowerCase()
      ) !== index
    );
    if (duplicateLabel) {
      showNotification?.(`❌ มีโซน/ราคาซ้ำ: ${duplicateLabel.label}`);
      return;
    }

    const normalized = normalizedRows.map(({ label, fee }) => ({ label, fee }));

    if (isCreateMode) {
      const duplicatedEvent = events.some(
        (event) =>
          !event.archived &&
          normalizeComparable(getEventName(event)).toLowerCase() ===
            normalizeComparable(normalizedName).toLowerCase()
      );
      if (duplicatedEvent) {
        showNotification?.("❌ มีงานชื่อเดียวกันที่กำลังใช้งานอยู่แล้ว");
        return;
      }

      setIsSavingEdit(true);
      setBusyId("__creating__");
      try {
        const eventId =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `event-${Date.now()}-${Math.random().toString(36).slice(2)}`;

        await setDoc(doc(db, "events", eventId), {
          name: normalizedName,
          eventName: normalizedName,
          tickets: normalized,
          prices: normalized,
          archived: false,
          archivedAt: null,
          createdAt: new Date(),
          updatedAt: new Date(),
        });

        setStatusFilter("active");
        setSearchTerm("");
        showNotification?.(`✅ เพิ่มงาน ${normalizedName} เรียบร้อยแล้ว`);
        setEditingEvent(null);
        setIsCreatingEvent(false);
        setEventNameDraft("");
        setEditingTickets([]);
      } catch (error) {
        console.error("เพิ่มงานไม่สำเร็จ", error);
        showNotification?.("❌ เพิ่มงานไม่สำเร็จ กรุณาลองใหม่");
      } finally {
        setIsSavingEdit(false);
        setBusyId("");
      }
      return;
    }

    // อัปเดตย้อนหลังเฉพาะ "แถวเดิม" ที่ถูกแก้จริงเท่านั้น
    // แถวที่เพิ่มใหม่ไม่มี originalLabel จึงไม่กระทบ record เก่า
    const changedExistingTickets = normalizedRows.filter((ticket) => {
      if (!ticket.isExisting || !ticket.originalLabel) return false;
      return (
        normalizeComparable(ticket.originalLabel) !== normalizeComparable(ticket.label) ||
        Number(ticket.originalFee) !== Number(ticket.fee)
      );
    });

    setIsSavingEdit(true);
    setBusyId(editingEvent.id);

    try {
      let updatedHistoricalRecords = 0;
      let skippedPaidRecords = 0;

      if (changedExistingTickets.length > 0) {
        const recordsSnapshot = await getDocs(collectionGroup(db, "records"));
        const eventName = normalizeComparable(getEventName(editingEvent));
        const recordUpdates = [];

        recordsSnapshot.docs.forEach((recordSnap) => {
          const record = recordSnap.data();
          const recordEventId = normalizeComparable(record.eventId);
          const recordEventName = normalizeComparable(record.eventName);
          const belongsToEvent = recordEventId
            ? recordEventId === editingEvent.id
            : recordEventName === eventName;

          if (!belongsToEvent) return;

          const recordZone = normalizeComparable(record.zone);
          const changedTicket = changedExistingTickets.find(
            (ticket) => normalizeComparable(ticket.originalLabel) === recordZone
          );
          if (!changedTicket) return;

          // รายการที่โอนเงินจริงไปแล้วถือเป็นหลักฐานทางการเงิน ห้ามแก้ยอดย้อนหลัง
          if (record.isPaid) {
            skippedPaidRecords += 1;
            return;
          }

          const recalculated = recalculateRecordAfterEventFeeChange(
            record,
            changedTicket.fee
          );

          recordUpdates.push({
            ref: recordSnap.ref,
            data: {
              ...recalculated,
              eventId: editingEvent.id,
              eventName: getEventName(editingEvent),
              zone: changedTicket.label,
              eventPricingUpdatedAt: new Date(),
            },
          });
        });

        for (const chunk of chunkArray(recordUpdates)) {
          const batch = writeBatch(db);
          chunk.forEach((item) => batch.update(item.ref, item.data));
          await batch.commit();
        }

        updatedHistoricalRecords = recordUpdates.length;
      }

      await updateDoc(doc(db, "events", editingEvent.id), {
        tickets: normalized,
        prices: normalized,
        updatedAt: new Date(),
      });

      if (updatedHistoricalRecords > 0 || skippedPaidRecords > 0) {
        const updatedText = updatedHistoricalRecords > 0
          ? `ปรับรายการที่ยังไม่โอน ${updatedHistoricalRecords} รายการ`
          : "ไม่มีรายการค้างที่ต้องปรับ";
        const paidText = skippedPaidRecords > 0
          ? ` · ล็อกรายการที่โอนแล้ว ${skippedPaidRecords} รายการไว้ตามยอดเดิม`
          : "";
        showNotification?.(`✅ อัปเดตงานแล้ว: ${updatedText}${paidText}`);
      } else {
        showNotification?.(
          "✅ อัปเดตงานแล้ว โซนที่เพิ่มใหม่ไม่กระทบรายการค่ากดเดิม"
        );
      }
      setEditingEvent(null);
      setIsCreatingEvent(false);
      setEventNameDraft("");
      setEditingTickets([]);
    } catch (error) {
      console.error("แก้ไขงานไม่สำเร็จ", error);
      showNotification?.("❌ แก้ไขงานไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setIsSavingEdit(false);
      setBusyId("");
    }
  };

  const toggleArchive = async (eventId, currentStatus) => {
    if (busyId) return;
    setBusyId(eventId);

    try {
      await updateDoc(doc(db, "events", eventId), {
        archived: !currentStatus,
        archivedAt: currentStatus ? null : new Date(),
        archiveRetentionDays: ARCHIVE_RETENTION_DAYS,
        updatedAt: new Date(),
      });

      showNotification?.(
        currentStatus
          ? "✅ เปิดใช้งานงานนี้แล้ว ทีมจะเห็นในหน้ากรอกค่ากด"
          : `📦 เก็บงานแล้ว ทีมจะไม่เห็นในหน้ากรอก และระบบจะลบตัวงานอัตโนมัติหลัง ${ARCHIVE_RETENTION_DAYS} วัน`
      );
    } catch (error) {
      console.error("เปลี่ยนสถานะงานไม่สำเร็จ", error);
      showNotification?.("❌ เปลี่ยนสถานะงานไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setBusyId("");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete || busyId) return;

    const eventId = pendingDelete.id;
    setBusyId(eventId);

    try {
      await deleteDoc(doc(db, "events", eventId));
      setPendingDelete(null);
      showNotification?.("🗑 ลบงานถาวรเรียบร้อยแล้ว");
    } catch (error) {
      console.error("ลบงานไม่สำเร็จ", error);
      showNotification?.("❌ ลบงานไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setBusyId("");
    }
  };

  const confirmPurgeArchived = async () => {
    if (isPurgingArchived || busyId) return;

    const archivedEvents = events.filter((event) => event.archived);
    if (archivedEvents.length === 0) {
      setShowPurgeArchivedConfirm(false);
      showNotification?.("ไม่มีงานที่เก็บแล้วให้ลบ");
      return;
    }

    setIsPurgingArchived(true);

    try {
      for (const chunk of chunkArray(archivedEvents)) {
        const batch = writeBatch(db);
        chunk.forEach((event) => batch.delete(doc(db, "events", event.id)));
        await batch.commit();
      }

      setShowPurgeArchivedConfirm(false);
      setStatusFilter("active");
      showNotification?.(
        `🧹 ลบงานที่เก็บแล้วทั้งหมด ${archivedEvents.length.toLocaleString("th-TH")} งานเรียบร้อยแล้ว`
      );
    } catch (error) {
      console.error("ลบงานที่เก็บแล้วทั้งหมดไม่สำเร็จ", error);
      showNotification?.("❌ ลบงานที่เก็บแล้วทั้งหมดไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setIsPurgingArchived(false);
    }
  };

  return (
    <div className="event-admin-page">
      <header className="event-admin-hero">
        <div>
          <span className="event-admin-eyebrow">EVENT MANAGEMENT</span>
          <h1>จัดการงาน</h1>
          <p>
            ดูงานที่ทีมเลือกกรอกได้ จัดการโซน และซ่อนงานที่จบแล้วโดยไม่ต้องลบข้อมูลย้อนหลัง
          </p>
        </div>
        <div className="event-admin-hero-actions">
          <div className="event-admin-live-pill">
            <span className="event-admin-live-dot" />
            อัปเดตจาก Firebase แบบเรียลไทม์
          </div>
          <button
            type="button"
            className="event-admin-create-button"
            onClick={openCreateEvent}
            disabled={Boolean(busyId) || isSavingEdit}
          >
            <FiPlus /> เพิ่มงาน
          </button>
        </div>
      </header>

      <section className="event-admin-stats" aria-label="สรุปรายการงาน">
        <article className="event-admin-stat event-admin-stat--primary">
          <span>เปิดใช้งาน</span>
          <strong>{summary.active.toLocaleString("th-TH")}</strong>
          <small>งานที่ทีมมองเห็นตอนนี้</small>
        </article>
        <article className="event-admin-stat">
          <span>งานทั้งหมด</span>
          <strong>{summary.total.toLocaleString("th-TH")}</strong>
          <small>รวมงานที่เก็บไว้ย้อนหลัง</small>
        </article>
        <article className="event-admin-stat">
          <span>เก็บเข้าคลัง</span>
          <strong>{summary.archived.toLocaleString("th-TH")}</strong>
          <small>ซ่อนจากหน้ากรอกค่ากด</small>
        </article>
        <article className="event-admin-stat">
          <span>โซน / ราคา</span>
          <strong>{summary.ticketOptions.toLocaleString("th-TH")}</strong>
          <small>ตัวเลือกทั้งหมดในทุกงาน</small>
        </article>
      </section>

      <section className="event-admin-toolbar">
        <label className="event-admin-search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            placeholder="ค้นหาชื่องาน โซน หรือค่ากด..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              aria-label="ล้างคำค้นหา"
            >
              ×
            </button>
          )}
        </label>

        <div className="event-admin-filters" role="group" aria-label="กรองสถานะงาน">
          {[
            ["active", "กำลังใช้", summary.active],
            ["all", "ทั้งหมด", summary.total],
            ["archived", "เก็บแล้ว", summary.archived],
          ].map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              className={statusFilter === key ? "is-active" : ""}
              onClick={() => setStatusFilter(key)}
            >
              {label}
              <span>{count}</span>
            </button>
          ))}
        </div>
      </section>

      <div className="event-admin-result-bar">
        <span>
          แสดง <strong>{filteredEvents.length.toLocaleString("th-TH")}</strong> งาน
        </span>
        <div className="event-admin-result-actions">
          {statusFilter === "active" && summary.archived > 0 && (
            <button type="button" onClick={() => setStatusFilter("archived")}>
              ดูงานที่เก็บไว้ →
            </button>
          )}
          {statusFilter === "archived" && summary.archived > 0 && (
            <button
              type="button"
              className="event-admin-purge-button"
              onClick={() => setShowPurgeArchivedConfirm(true)}
            >
              <FiTrash2 />
              ลบงานที่เก็บแล้วทั้งหมด ({summary.archived.toLocaleString("th-TH")})
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="event-admin-state">
          <FiRefreshCw className="event-admin-spin" />
          <strong>กำลังโหลดรายการงาน</strong>
          <span>ดึงข้อมูลล่าสุดจาก Firebase</span>
        </div>
      ) : loadError ? (
        <div className="event-admin-state event-admin-state--error">
          <strong>โหลดข้อมูลไม่สำเร็จ</strong>
          <span>{loadError}</span>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="event-admin-state">
          <FiSearch />
          <strong>ไม่พบงานในมุมมองนี้</strong>
          <span>ลองเปลี่ยนคำค้นหาหรือเลือกสถานะอื่น</span>
        </div>
      ) : (
        <section className="event-admin-list">
          {filteredEvents.map((event) => {
            const eventName = getEventName(event);
            const tickets = getTickets(event);
            const isExpanded = expandedIds.has(event.id);
            const feeValues = tickets
              .map((ticket) => Number(ticket?.fee))
              .filter(Number.isFinite);
            const minFee = feeValues.length ? Math.min(...feeValues) : null;
            const maxFee = feeValues.length ? Math.max(...feeValues) : null;
            const isBusy = busyId === event.id;

            return (
              <article
                key={event.id}
                className={`event-admin-card ${
                  event.archived ? "event-admin-card--archived" : ""
                }`}
              >
                <div className="event-admin-card-main">
                  <button
                    type="button"
                    className="event-admin-expand"
                    onClick={() => toggleExpanded(event.id)}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? "ซ่อน" : "ดู"}รายละเอียด ${eventName}`}
                  >
                    <span className="event-admin-event-icon">
                      {event.archived ? <FiArchive /> : <FiLayers />}
                    </span>

                    <span className="event-admin-card-copy">
                      <span className="event-admin-title-row">
                        <strong>{eventName}</strong>
                        <span
                          className={`event-admin-status ${
                            event.archived ? "is-archived" : "is-active"
                          }`}
                        >
                          {event.archived ? "เก็บแล้ว" : "กำลังใช้"}
                        </span>
                      </span>

                      <span className="event-admin-meta">
                        <span>{tickets.length.toLocaleString("th-TH")} โซน/ราคา</span>
                        <i />
                        <span>
                          {minFee === null
                            ? "ยังไม่มีค่ากด"
                            : minFee === maxFee
                            ? `ค่ากด ${formatMoney(minFee)}`
                            : `ค่ากด ${formatMoney(minFee)}–${formatMoney(maxFee)}`}
                        </span>
                        {event.archived && (
                          <>
                            <i />
                            <span>ลบอัตโนมัติใน {getArchiveDaysLeft(event)} วัน</span>
                          </>
                        )}
                      </span>
                    </span>

                    <span className="event-admin-chevron">
                      {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                    </span>
                  </button>

                  <div className="event-admin-actions">
                    <button
                      type="button"
                      className="is-edit"
                      disabled={isBusy}
                      onClick={() => openEditEvent(event)}
                    >
                      <FiEdit3 />
                      แก้ไข
                    </button>

                    <button
                      type="button"
                      className={event.archived ? "is-restore" : "is-archive"}
                      disabled={isBusy}
                      onClick={() => toggleArchive(event.id, Boolean(event.archived))}
                    >
                      {isBusy ? (
                        <FiRefreshCw className="event-admin-spin" />
                      ) : event.archived ? (
                        <FiUnlock />
                      ) : (
                        <FiArchive />
                      )}
                      {event.archived ? "เปิดใช้งาน" : "เก็บงาน"}
                    </button>

                    <button
                      type="button"
                      className="is-delete"
                      disabled={isBusy}
                      onClick={() => setPendingDelete(event)}
                      title="ลบงานถาวร"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="event-admin-details">
                    <div className="event-admin-details-head">
                      <div>
                        <span>โซนและค่ากด</span>
                        <small>
                          ข้อมูลชุดนี้คือสิ่งที่ทีมจะเห็นตอนเลือกงานในหน้ากรอกค่ากด
                        </small>
                      </div>
                      <strong>{tickets.length.toLocaleString("th-TH")} รายการ</strong>
                    </div>

                    {tickets.length === 0 ? (
                      <div className="event-admin-empty-tickets">
                        งานนี้ยังไม่มีโซนหรือค่ากดที่ตั้งไว้
                      </div>
                    ) : (
                      <div className="event-admin-ticket-grid">
                        {tickets.map((ticket, index) => (
                          <div
                            key={`${event.id}-${ticket?.label || "ticket"}-${index}`}
                            className="event-admin-ticket"
                          >
                            <span>
                              {String(ticket?.label || "ไม่ระบุโซน").trim() || "ไม่ระบุโซน"}
                            </span>
                            <strong>{formatMoney(ticket?.fee)}</strong>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="event-admin-info-note">
                      {event.archived ? (
                        <>
                          <FiArchive /> งานนี้ถูกเก็บไว้ ทีมจะไม่เห็นในรายการงานใหม่ และตัวงานจะถูกลบอัตโนมัติหลังครบ {ARCHIVE_RETENTION_DAYS} วัน โดยประวัติค่ากดเดิมยังอยู่
                        </>
                      ) : (
                        <>
                          <FiLayers /> งานนี้กำลังเปิดใช้งาน ถ้าแก้โซน/ราคาเดิม ระบบจะปรับรายการค่ากดเก่าที่ตรงกับโซนนั้นให้ด้วย ส่วนโซนที่เพิ่มใหม่จะไม่กระทบข้อมูลเดิม
                        </>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      )}

      {(editingEvent || isCreatingEvent) && (
        <div
          className="event-admin-modal-backdrop event-admin-editor-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isSavingEdit) {
              closeEventEditor();
            }
          }}
        >
          <div
            className="event-admin-modal event-admin-edit-modal event-admin-editor-panel"
            role="dialog"
            aria-modal="true"
          >
            <div className="event-admin-edit-header">
              <div>
                <span className="event-admin-edit-eyebrow">
                  {isCreatingEvent ? "NEW EVENT" : "EDIT EVENT"}
                </span>
                <h2>{isCreatingEvent ? "เพิ่มงานใหม่" : "แก้ไขโซน / ราคา / ค่ากด"}</h2>
                <p>
                  {isCreatingEvent
                    ? "สร้างงานใหม่จากหน้านี้ได้เลย ไม่ต้องสลับเมนู"
                    : getEventName(editingEvent)}
                </p>
              </div>
              <button
                type="button"
                className="event-admin-modal-close"
                aria-label="ปิดหน้าต่างจัดการงาน"
                disabled={isSavingEdit}
                onClick={closeEventEditor}
              >
                <FiX />
              </button>
            </div>

            {isCreatingEvent && (
              <label className="event-admin-name-field">
                <span>ชื่องาน</span>
                <input
                  type="text"
                  value={eventNameDraft}
                  disabled={isSavingEdit}
                  placeholder="เช่น NCT BANGKOK"
                  autoFocus
                  onChange={(event) => setEventNameDraft(event.target.value)}
                />
              </label>
            )}

            <div className={`event-admin-edit-note ${isCreatingEvent ? "is-create" : ""}`}>
              {isCreatingEvent
                ? "ใส่โซน/ราคาบัตรและค่ากดต่อใบได้หลายรายการ พอบันทึกแล้วงานจะเปิดให้ทีมเลือกทันที"
                : "แก้ได้แม้งานกำลังใช้งานอยู่ ถ้าแก้แถวโซน/ราคาเดิม ระบบจะปรับค่ากดของรายการเก่าที่ตรงกับโซนนั้นให้ด้วย แต่การเพิ่มโซนใหม่จะไม่กระทบข้อมูลที่ทีมเคยกรอก"}
            </div>

            <div className="event-admin-edit-table-head" aria-hidden="true">
              <span>โซน / ราคาบัตร</span>
              <span>ค่ากด / ใบ</span>
              <span />
            </div>

            <div className="event-admin-edit-rows">
              {editingTickets.map((ticket, index) => (
                <div
                  className="event-admin-edit-row"
                  key={`${isCreatingEvent ? "new" : editingEvent?.id || "edit"}-${index}`}
                >
                  <label>
                    <span>โซน / ราคาบัตร</span>
                    <input
                      type="text"
                      value={ticket.label}
                      disabled={isSavingEdit}
                      placeholder="เช่น A1, VIP, 5900"
                      onChange={(event) =>
                        updateEditingTicket(index, "label", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    <span>ค่ากด / ใบ</span>
                    <div className="event-admin-money-input">
                      <span>฿</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={ticket.fee}
                        disabled={isSavingEdit}
                        placeholder="650"
                        onChange={(event) =>
                          updateEditingTicket(index, "fee", event.target.value)
                        }
                      />
                    </div>
                  </label>
                  <button
                    type="button"
                    className="event-admin-remove-ticket"
                    title="ลบโซนนี้"
                    aria-label={`ลบรายการที่ ${index + 1}`}
                    disabled={isSavingEdit}
                    onClick={() => removeEditingTicket(index)}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="event-admin-add-ticket"
              disabled={isSavingEdit}
              onClick={addEditingTicket}
            >
              <FiPlus /> เพิ่มโซน / ราคา
            </button>

            <div className="event-admin-editor-preview">
              <div>
                <span>รายการ</span>
                <strong>{editingTickets.length.toLocaleString("th-TH")}</strong>
              </div>
              <div>
                <span>ค่ากดที่กรอกแล้ว</span>
                <strong>
                  {editingTickets.filter((ticket) => String(ticket.fee ?? "").trim() !== "").length.toLocaleString("th-TH")}
                </strong>
              </div>
            </div>

            <div className="event-admin-edit-footer">
              <span>
                {isCreatingEvent
                  ? "บันทึกแล้วทีมจะเห็นงานนี้ทันที"
                  : `${editingTickets.length.toLocaleString("th-TH")} รายการ`}
              </span>
              <div>
                <button
                  type="button"
                  className="is-cancel"
                  disabled={isSavingEdit}
                  onClick={closeEventEditor}
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  className="is-save-edit"
                  disabled={isSavingEdit}
                  onClick={saveEventEditor}
                >
                  {isSavingEdit ? (
                    <FiRefreshCw className="event-admin-spin" />
                  ) : isCreatingEvent ? (
                    <FiPlus />
                  ) : (
                    <FiEdit3 />
                  )}
                  {isSavingEdit
                    ? "กำลังบันทึก..."
                    : isCreatingEvent
                    ? "บันทึกงาน"
                    : "บันทึกการแก้ไข"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {showPurgeArchivedConfirm && (
        <div
          className="event-admin-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isPurgingArchived) {
              setShowPurgeArchivedConfirm(false);
            }
          }}
        >
          <div className="event-admin-modal" role="dialog" aria-modal="true">
            <span className="event-admin-modal-icon">
              <FiTrash2 />
            </span>
            <h2>ลบงานที่เก็บแล้วทั้งหมด?</h2>
            <p>
              จะลบตัวงานที่อยู่ในสถานะ <strong>เก็บแล้ว {summary.archived.toLocaleString("th-TH")} งาน</strong> ออกจาก collection <strong>events</strong> ทันที
              แต่ประวัติค่ากด รายงาน และยอดของทีมที่เคยบันทึกไว้จะยังอยู่เหมือนเดิม
            </p>
            <div className="event-admin-modal-actions">
              <button
                type="button"
                className="is-cancel"
                disabled={isPurgingArchived}
                onClick={() => setShowPurgeArchivedConfirm(false)}
              >
                ยกเลิก
              </button>
              <button
                type="button"
                className="is-confirm-delete"
                disabled={isPurgingArchived}
                onClick={confirmPurgeArchived}
              >
                {isPurgingArchived ? <FiRefreshCw className="event-admin-spin" /> : <FiTrash2 />}
                {isPurgingArchived ? "กำลังลบ..." : "ลบทั้งหมดตอนนี้"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div
          className="event-admin-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busyId) {
              setPendingDelete(null);
            }
          }}
        >
          <div className="event-admin-modal" role="dialog" aria-modal="true">
            <span className="event-admin-modal-icon">
              <FiTrash2 />
            </span>
            <h2>ลบงานถาวร?</h2>
            <p>
              กำลังจะลบ <strong>{getEventName(pendingDelete)}</strong> ออกจากรายการงาน
              ถ้าต้องการแค่ไม่ให้ทีมเห็น แนะนำให้ใช้ “เก็บงาน” แทน
            </p>
            <div className="event-admin-modal-actions">
              <button
                type="button"
                className="is-cancel"
                disabled={Boolean(busyId)}
                onClick={() => setPendingDelete(null)}
              >
                ยกเลิก
              </button>
              <button
                type="button"
                className="is-confirm-delete"
                disabled={Boolean(busyId)}
                onClick={confirmDelete}
              >
                {busyId ? <FiRefreshCw className="event-admin-spin" /> : <FiTrash2 />}
                ลบถาวร
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
